import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, Input, Select } from '@/ds';
import { DateField, MoneyField } from '@/components/fields';
import { Provenance } from '@/components/money';
import { ApiError } from '@/api/client';
import { useOperation, type FeeKind, type WalletSummary } from '@/api/hooks';
import { formatMoney, formatNumber } from '@/lib/format';
import {
  dateInputToIso,
  minorToInput,
  parseMinor,
  parsePercent,
  parseRate,
  suggestPaidAmount,
  todayInputValue,
} from '@/lib/money';
import { useControlSize } from '@/lib/useControlSize';

const FEE_OPTIONS = [
  { value: 'none', label: 'Без комиссии' },
  { value: 'percent', label: 'Процент' },
  { value: 'fixed', label: 'Фиксированная' },
];

const PAID_CURRENCIES = [
  { value: 'RUB', label: 'Рубли' },
  { value: 'USDT', label: 'USDT' },
  { value: 'USD', label: 'Доллары' },
];

export function WalletTopUpDialog({
  wallet,
  open,
  onClose,
}: {
  wallet: WalletSummary;
  open: boolean;
  onClose: () => void;
}) {
  const size = useControlSize();
  const create = useOperation<{
    walletId: string;
    occurredAt: string;
    creditedAmount: number;
    paidAmount: number;
    paidCurrency: string;
    feeKind: FeeKind;
    feeValue: number;
    comment?: string;
  }>('wallet_topup.create');

  const [occurredAt, setOccurredAt] = useState(todayInputValue());
  const [credited, setCredited] = useState('');
  const [paidCurrency, setPaidCurrency] = useState('RUB');
  const [rate, setRate] = useState('');
  const [feeKind, setFeeKind] = useState<FeeKind>('none');
  const [fee, setFee] = useState('');
  const [paid, setPaid] = useState('');
  const [paidTouched, setPaidTouched] = useState(false);
  const [comment, setComment] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const creditedMinor = parseMinor(credited);
  const rateValue = parseRate(rate);
  const feeMinor = feeKind === 'percent' ? parsePercent(fee) : parseMinor(fee);
  const paidMinor = parseMinor(paid);

  // Подсказка суммы списания. Итог остаётся редактируемым: фактическое
  // списание важнее расчётного (docs/04-finance.md).
  const suggested = useMemo(() => {
    if (creditedMinor === null || rateValue === null) return null;
    return suggestPaidAmount(creditedMinor, rateValue, feeKind, feeMinor ?? 0);
  }, [creditedMinor, rateValue, feeKind, feeMinor]);

  useEffect(() => {
    if (paidTouched || suggested === null) return;
    setPaid(minorToInput(suggested));
  }, [suggested, paidTouched]);

  useEffect(() => {
    if (!open) return;
    setOccurredAt(todayInputValue());
    setCredited('');
    setRate('');
    setFeeKind('none');
    setFee('');
    setPaid('');
    setPaidTouched(false);
    setComment('');
    setLocalError(null);
    create.reset();
    // Сброс делается один раз при открытии.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const effectiveRate =
    creditedMinor && creditedMinor > 0 && paidMinor !== null ? paidMinor / creditedMinor : null;

  const serverError = create.error instanceof ApiError ? create.error.message : null;

  function submit() {
    setLocalError(null);
    const iso = dateInputToIso(occurredAt);
    if (!iso) {
      setLocalError('Укажите дату операции');
      return;
    }
    if (creditedMinor === null || creditedMinor <= 0) {
      setLocalError('Укажите, сколько зачислено на кошелёк');
      return;
    }
    if (paidMinor === null || paidMinor <= 0) {
      setLocalError('Укажите, сколько списали фактически');
      return;
    }

    create.mutate(
      {
        walletId: wallet.id,
        occurredAt: iso,
        creditedAmount: creditedMinor,
        paidAmount: paidMinor,
        paidCurrency,
        feeKind,
        feeValue: feeMinor ?? 0,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      open={open}
      title="Пополнить кошелёк"
      description={`Зачисление на «${wallet.name}» в ${wallet.currency} и фактическое списание`}
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Отмена
          </Button>
          <Button size={size} onClick={submit} loading={create.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
          <DateField label="Дата операции" value={occurredAt} onChange={setOccurredAt} />
          <MoneyField
            label={`Зачислено, ${wallet.currency}`}
            value={credited}
            onChange={setCredited}
            autoFocus
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
          <Select
            size={size}
            label="Валюта платежа"
            options={PAID_CURRENCIES}
            value={paidCurrency}
            onChange={(e) => setPaidCurrency(e.target.value)}
          />
          <MoneyField
            label={`Курс, ${paidCurrency} за 1 ${wallet.currency}`}
            value={rate}
            onChange={setRate}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-5)' }}>
          <Select
            size={size}
            label="Комиссия"
            options={FEE_OPTIONS}
            value={feeKind}
            onChange={(e) => setFeeKind(e.target.value as FeeKind)}
          />
          {feeKind === 'none' ? (
            <div />
          ) : (
            <MoneyField
              label={feeKind === 'percent' ? 'Процент комиссии' : `Комиссия, ${paidCurrency}`}
              value={fee}
              onChange={setFee}
            />
          )}
        </div>

        <MoneyField
          label={`Итого списано, ${paidCurrency}`}
          value={paid}
          onChange={(value) => {
            setPaidTouched(true);
            setPaid(value);
          }}
          help={
            suggested !== null && !paidTouched
              ? 'Посчитано по курсу и комиссии, можно поправить'
              : 'Фактическое списание важнее расчётного'
          }
        />

        {effectiveRate !== null ? (
          <Provenance>
            {`Эффективный курс: ${formatNumber(effectiveRate, 2)} ${paidCurrency === 'RUB' ? '₽' : paidCurrency} за 1 ${wallet.currency}. `}
            {suggested !== null && paidMinor !== null && suggested !== paidMinor
              ? `Расчётное списание было ${formatMoney(suggested, paidCurrency)}.`
              : ''}
          </Provenance>
        ) : null}

        <Input
          size={size}
          label="Комментарий"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          help="Необязательно: банк, способ перевода, особенности"
        />

        {localError ?? serverError ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
            {localError ?? serverError}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
